import { action, observable, computed, reaction } from 'mobx';
import Model from './Model.js';

/**
 * Хранилище моделей
 * Может находить в нескольких состояниях
 *   isConnected	- данные о моделях получены, подключено к серверу
 *   isLoading		- выполняется запрос на сервер
 *   isAuthorized	- хранилище авторизовано
 *   isOnline		- есть соединение с сервером
 */
export default class ModelStore {

	/**
	 * Признак инициализации хранилища
	 */
	@observable isInitialized = false;

	/**
	 * Признак авторизации на сервере
	 */
	@observable isAuthorized = false;

	/**
	 * Текущий пользователь
	 */
	@observable user;

	/**
	 * Роли текущегоо пользователя
	 */
	@observable roles;

	/**
	 * Последняя ошибка полученная с последнего запроска на сервер
	 */
	@observable error;

	/**
	 * API for fetching data from store
	 */
	_transport;

	/**
	 * Normalized object cache: cache [modelName][objectId] = observabele object
	 */
	_cache = {};

	/**
	 * Models cache
	 */
	_models = {};

	/**
	 * Автоматичкское подключение к серверу
	 */
	_autoConnect = true;
	
	_useCache = true;

	/**
	 * Функция которая вызывается при успешной авторизации.
	 * Нужна, к примеру, для того чтобы перелогинить вебсокет после успешной авторизации.
	 * Передается один аргумент - авторизованный пользователь.
	 */
	_onLogin;

	/**
	 * Создает новое хранилищае
	 * @param  {Object}  transport Транспорт по которому происходит работа с сервером
	 * @param  {Boolean} init Инициализировать ли store, если false то можно вручную вызвать store.init ()
	 */
	constructor ({ transport, cache = true, autoConnect = true, onLogin }) {
		this._transport = transport;
		this._autoConnect = autoConnect;
		this._useCache = cache;
		this._autoConnect && this.connect ();
		this._onLogin = onLogin;
	}

	/**
	 * Инициализация store, вовзращает Promise
	 */
	@action async connect (onInit) {
		try {
			// получение информации о сервере
			let info = await this._transport.connect ();

			// если соединение произошло очищаем состояния хранилища
			this._reset ();

			// инициализация моделей
			info.models.forEach (modelInfo => {
				this._models [modelInfo.name] = this [modelInfo.name] = Model.INIT (this, this._transport, modelInfo);
			}, this);

			// информация о ролях текущего пользователи (сессии, так как есть динамические роли аля $everyone)
			if (info.roles) {
				this.roles = info.roles.map(role => role.id ?
					(this.Role ? this.Role.PROCESS_INSTANCE_CACHE(role) : role) : // если модели Role нет
					role
				); //new this.Role (info.roles);
			}

			// конфиг сервера
			if (info.config) {
				this.config = info.config;
			}

			// информация о текущем пользователе если уже был авторизован
			if (info.user) {
				this.user = this.User.PROCESS_INSTANCE_CACHE(info.user); //new this.User (info.user);
				this.isAuthorized = true;
			}

			this.isInitialized = true;
			if (onInit) onInit(this);
			this.error = null;
		} catch (error) {
			console.log (error);
			this.error = error;
		}
	}

	/**
	 * Признак того что хранилище сейчас выполняет запрос
	 */
	@computed get isLoading () {
		return this._transport.isLoading;
	}

	/**
	 * Запрос авторизации на сервере
	 */
	@action async login (email, password, captcha) {
		try {
			if (this.User) {
				this.user = await this.User.login ({
					email: email.indexOf ('@') !== -1 ? email : undefined,
					username: email.indexOf ('@') === -1 ? email : undefined,
					password,
					captcha
				});
				await this.connect ();
				this._onLogin && this._onLogin(this.user);
				// this.isAuthorized = true;
			}
		} catch (error) {
			this.error = error;
			this.user = null;
			this.isAuthorized = false;
			throw error;
		}
	}

	@action async logout () {
		try {
			this.User && await this.User.logout ();
			this.isAuthorized = false;
		} catch (error) {
			this.error = error;
			// throw error;
		} finally {
			await this.connect ();
			// this._reset ();
		}
	}

	/**
	 * Регистрация нового пользователя
	 */
	@action async register (user) {
		try {
			await this.User.create (user);
			this.error = 'User.error.email.verifySent';
		} catch (error) {
			this.error = error;
			throw error;
		}
	}

	/**
	 * Полная очистка состояния хранилища, используется для повторной инициализации если что-то
	 * пошло не так
	 */
	@action _reset () {
		this._cache = {};
		this._models && Object.keys (this._models).forEach (modelName => {
			delete this [modelName];
		});
		this._models = {};
		this.isInitialized = false;
		this.isAuthorized = false;
		this.user = null;
	}

	/**
	 * Синхронизация локальных изменений объектов и изменений данных на сервере
	 */
	@action async sync () {
		let models = Object.keys (this._models);

		for (let modelName of models) {
			await (this._models [modelName].SYNC ());
		}
	}
	
	@action uploadFile (model, id, property, file) {
		this._transport._pendingRequestCount ++;
		return this._transport.uploadFile (model.INFO.path.toLowerCase(), id, property, file, model.INFO.container);
	}
	
	@action downloadFile (model, id, property) {
		return this._transport.downloadFile (model.INFO.path.toLowerCase(), id, property, model.INFO.container);
	}
	
	@action deleteFile (model, id, property) {
		return this._transport.deleteFile (model.INFO.path.toLowerCase(), id, property, model.INFO.container);
	}
	
};
